import React from 'react';
import RuntimeConfigFields from './RuntimeConfigFields';
import { RuntimeConfig, RuntimeKeyTypes } from '../../../../types/rest/mockServiceTypes';
import _ from 'lodash';
import { Col, Row } from 'react-bootstrap';
import styles from './RuntimeConfigTab.module.css';
import clsx from 'clsx';

interface RuntimeConfigTabProps {
  state: RuntimeConfigState;
  updateState(runtimeConfigState: RuntimeConfigState): void;
  currenTab: string;
}

export interface RuntimeConfigState {
  runtimeConfig: RuntimeConfig;
}

const RuntimeConfigTab = (props: RuntimeConfigTabProps) => {
  const handleChange = (id: string, newValue: any) => {
    const currentRuntimeConfig: RuntimeConfig = _.cloneDeep(props.state.runtimeConfig);
    currentRuntimeConfig[id] = newValue;
    props.updateState({ runtimeConfig: currentRuntimeConfig });
  };

  interface RuntimeConfigView {
    type: string;
    title: string;
    description: string;
    errorMessage?: string;
    validation?: (value: any) => boolean;
  }

  const verifyValue = (value: any, minValue: number, maxValue: number) => {
    const numericValue = _.isString(value) ? parseFloat(value) : value;
    const isValidNumber = /^-?\d+(\.\d+)?$/.test(value);
    return isValidNumber && _.isNumber(numericValue) && numericValue >= minValue && numericValue <= maxValue;
  };

  const RuntimeConfDictionary: {
    [key: string]: RuntimeConfigView;
  } = {
    [RuntimeKeyTypes.showLogsEnabled]: {
      title: 'Show Logs Enabled',
      description:
        'Enable or disable show logs feature. Setting this to false can improve performance by approximately 5%.',
      type: 'boolean',
    },
    [RuntimeKeyTypes.showLogsMaxSize]: {
      title: 'Show Logs Max Size',
      description: 'Show Logs Max Size (between 0 and 100)',
      errorMessage: 'Number must be between 0 and 100',
      type: 'number',
      validation: (value: any) => {
        return verifyValue(value, 0, 100);
      },
    },
    [RuntimeKeyTypes.inspectionDataEnabled]: {
      title: 'Inspection Data Enabled',
      description:
        'Enable or disable inspection data feature. Setting this to false can improve performance by approximately 5%.',
      type: 'boolean',
    },
    [RuntimeKeyTypes.inspectionDataMaxRequestSize]: {
      title: 'Max Number of Stored Inspection Data Requests',
      description: 'Maximum data request size (between 0 and 100)',
      errorMessage: 'Number must be between 0 and 100',
      type: 'number',
      validation: (value: any) => {
        return verifyValue(value, 0, 100);
      },
    },
    [RuntimeKeyTypes.inspectionDataMaxRequestsPerSecond]: {
      title: 'Inspection Data Max Requests Per Second',
      description: 'Maximum requests per second to inspect (between 0 and 5)',
      errorMessage: 'Number must be between 0 and 5',
      type: 'number',
      validation: (value: any) => {
        return verifyValue(value, 0, 5);
      },
    },
    [RuntimeKeyTypes.inspectionDataMaxActionsPerSecond]: {
      title: 'Inspection Data Max Actions Per Second',
      description: 'Maximum number of actions per second (between 0 and 50)',
      errorMessage: 'Number must be between 0 and 50',
      type: 'number',
      validation: (value: any) => {
        return verifyValue(value, 0, 50);
      },
    },
    [RuntimeKeyTypes.inspectionDataMaxBodySize]: {
      title: 'Inspection Data Max Body Size',
      description: 'Maximum size of the body to inspect (between 0 and 10240000)',
      errorMessage: 'Number must be between 0 and 10240000',
      type: 'number',
      validation: (value: any) => {
        return verifyValue(value, 0, 10240000);
      },
    },
    [RuntimeKeyTypes.matcherLogsCacheMaxSize]: {
      title: 'Matcher Logs Cache Max Size',
      description: 'Maximum size of the matcher logs cache (between 1 and 1000)',
      errorMessage: 'Number must be between 1 and 1000',
      type: 'number',
      validation: (value: any) => {
        return verifyValue(value, 1, 1000);
      },
    },
    [RuntimeKeyTypes.consoleMatcherLogsEnabled]: {
      title: 'Console Matcher Logs Enabled',
      description:
        'Enable or disable console matcher logs. Setting this to true can significantly decrease performance.',
      type: 'boolean',
    },
    [RuntimeKeyTypes.showLogsMatcherLogsEnabled]: {
      title: 'Show Logs Matcher Logs Enabled',
      description:
        'Enable or disable show logs matcher logs. Setting this to true can significantly decrease performance.',
      type: 'boolean',
    },
    [RuntimeKeyTypes.cacheStaticResponses]: {
      title: 'Caching Enabled',
      description:
        'Enable or disable caching of static responses. When enabled, repeated requests for the same ' +
        'static content are served faster, reducing response times and improving overall performance.',
      type: 'boolean',
    },
    [RuntimeKeyTypes.secretsEnabled]: {
      title: 'Secrets Enabled',
      description: 'Enable or disable secrets handling.',
      type: 'boolean',
    },
    [RuntimeKeyTypes.scriptsEnabled]: {
      title: 'Scripts Enabled',
      description: 'Enable or disable processing of JavaScript in templates.',
      type: 'boolean',
    },
    [RuntimeKeyTypes.nodejsEnabled]: {
      title: 'NodeJS Enabled',
      description:
        'Enable or disable NodeJS processing of JavaScript in templates. Always disabled for cloud locations.',
      type: 'boolean',
    },
    [RuntimeKeyTypes.jettyContainerThreads]: {
      title: 'Web Server Container Threads',
      description:
        'Optional number of threads in the web server (between 12 and 1000). The default value for web server threads is the number of processor cores × 32.',
      errorMessage: 'Number must be between 12 and 1000',
      type: 'number | null',
      validation: (value: any) => {
        if (value === null || value === '') {
          return true;
        }

        return verifyValue(value, 12, 1000);
      },
    },
    [RuntimeKeyTypes.requestTimeout]: {
      title: 'Request Timeout',
      description: 'Timeout for requests in ms (between 0 and 300000 ms)',
      errorMessage: 'Number must be between 0 and 300000',
      type: 'number',
      validation: (value: any) => {
        return verifyValue(value, 0, 300000);
      },
    },
  };

  const shouldDisableField = (key: string): boolean => {
    if (!props.state.runtimeConfig) {
      return true;
    }

    switch (key) {
      case RuntimeKeyTypes.showLogsMaxSize:
        return String(props.state.runtimeConfig[RuntimeKeyTypes.showLogsEnabled]).toLowerCase() === 'false';
      case RuntimeKeyTypes.inspectionDataMaxRequestSize:
      case RuntimeKeyTypes.inspectionDataMaxRequestsPerSecond:
      case RuntimeKeyTypes.inspectionDataMaxActionsPerSecond:
      case RuntimeKeyTypes.inspectionDataMaxBodySize:
        return String(props.state.runtimeConfig[RuntimeKeyTypes.inspectionDataEnabled]).toLowerCase() === 'false';
      case RuntimeKeyTypes.matcherLogsCacheMaxSize:
        return (
          String(props.state.runtimeConfig[RuntimeKeyTypes.consoleMatcherLogsEnabled]).toLowerCase() === 'false' &&
          String(props.state.runtimeConfig[RuntimeKeyTypes.showLogsMatcherLogsEnabled]).toLowerCase() === 'false'
        );
      case RuntimeKeyTypes.nodejsEnabled:
        return String(props.state.runtimeConfig[RuntimeKeyTypes.scriptsEnabled]).toLowerCase() === 'false';
      default:
        return false;
    }
  };

  const getConfigView = (key: string) => {
    const fieldValue = props.state.runtimeConfig ? props.state.runtimeConfig[key] : '';
    const runtimeProp: RuntimeConfigView = RuntimeConfDictionary[key];
    return (
      <RuntimeConfigFields
        key={key}
        id={key}
        title={runtimeProp.title}
        description={runtimeProp.description}
        errorMessage={runtimeProp.errorMessage}
        value={fieldValue}
        isReadOnly={shouldDisableField(key)}
        keyType={runtimeProp.type}
        onChange={(value: any) => {
          handleChange(key, value);
        }}
        validation={runtimeProp.validation}
        showLastValidState={props.currenTab === 'runtimeSettings'}
      />
    );
  };

  return (
    <div className={'px-4 pt-1'}>
      <p>
        Optimize the performance of your virtual service by adjusting the available runtime properties. These settings
        allow you to fine-tune various aspects of the virtual service, ensuring an enhanced user experience tailored to
        your specific needs. You can customize the runtime configurations for better speed, responsiveness, and
        efficiency.
      </p>
      <Row>
        {/*Show Logs*/}
        <Col md={3} className={'px-4'}>
          <h4 className={'mb-4'}>Show Logs</h4>
          <div>{getConfigView(RuntimeKeyTypes.showLogsEnabled)}</div>
          <div>{getConfigView(RuntimeKeyTypes.showLogsMaxSize)}</div>
          <h4 className={'mt-6 mb-4'}>Static response caching</h4>
          <div>{getConfigView(RuntimeKeyTypes.cacheStaticResponses)}</div>

          <h4 className={'mt-6 mb-4'}>Secrets</h4>
          <div>{getConfigView(RuntimeKeyTypes.secretsEnabled)}</div>
        </Col>

        {/*Inspection Data*/}
        <Col md={3} className={clsx(styles.separator, 'px-7')}>
          <h4 className={'mb-4'}>Inspection Data</h4>
          <div>{getConfigView(RuntimeKeyTypes.inspectionDataEnabled)}</div>
          <div>{getConfigView(RuntimeKeyTypes.inspectionDataMaxRequestSize)}</div>
          <div>{getConfigView(RuntimeKeyTypes.inspectionDataMaxRequestsPerSecond)}</div>
          <div>{getConfigView(RuntimeKeyTypes.inspectionDataMaxActionsPerSecond)}</div>
          <div>{getConfigView(RuntimeKeyTypes.inspectionDataMaxBodySize)}</div>
        </Col>

        {/* Matcher Logs */}
        <Col md={3} className={clsx(styles.separator, 'px-7')}>
          <h4 className={'mb-4'}>Matcher Logs</h4>
          <div>{getConfigView(RuntimeKeyTypes.consoleMatcherLogsEnabled)}</div>
          <div>{getConfigView(RuntimeKeyTypes.showLogsMatcherLogsEnabled)}</div>
          <div>{getConfigView(RuntimeKeyTypes.matcherLogsCacheMaxSize)}</div>

          <h4 className={'mt-6 mb-4'}>Scripting</h4>
          <div>{getConfigView(RuntimeKeyTypes.scriptsEnabled)}</div>
          <div>{getConfigView(RuntimeKeyTypes.nodejsEnabled)}</div>
        </Col>

        {/*Jetty Settings*/}
        <Col md={3} className={clsx(styles.separator, 'px-7')}>
          <h4 className={'mb-4'}>Web Server Configuration</h4>
          <div>{getConfigView(RuntimeKeyTypes.jettyContainerThreads)}</div>
          <div>{getConfigView(RuntimeKeyTypes.requestTimeout)}</div>
        </Col>
      </Row>
    </div>
  );
};

export default RuntimeConfigTab;
